/**
 * Multi-mod helpers that mirror the backend's `attachScheduleModerators`
 * contract: each schedule row carries `primary_moderator_ids[]` and
 * `secondary_moderator_ids[]` arrays, plus the legacy single-id aliases
 * (`moderator_id`, `secondary_moderator_id`) which are kept as lead-of-array
 * back-compat fields during rollout.
 */

type AnySchedule =
  | {
      primary_moderator_ids?: string[] | null;
      secondary_moderator_ids?: string[] | null;
      primary_moderator_names?: string[] | null;
      moderator_id?: string | null;
      secondary_moderator_id?: string | null;
    }
  | null
  | undefined;

/** True if `userId` is assigned to `schedule` in any role (primary or secondary). */
export const isUserAssignedToSchedule = (
  schedule: AnySchedule,
  userId: string | null | undefined,
): boolean => {
  if (!schedule || !userId) return false;
  if (
    Array.isArray(schedule.primary_moderator_ids) &&
    schedule.primary_moderator_ids.includes(userId)
  )
    return true;
  if (
    Array.isArray(schedule.secondary_moderator_ids) &&
    schedule.secondary_moderator_ids.includes(userId)
  )
    return true;
  if (schedule.moderator_id === userId) return true;
  if (schedule.secondary_moderator_id === userId) return true;
  return false;
};

/**
 * True if `userId` is the *primary* moderator on `schedule` — reading the
 * `primary_moderator_ids` array with the legacy `moderator_id` alias as a
 * fallback. A user assigned only as a secondary moderator does NOT match, so
 * this scopes a schedule to the moderator who owns the session.
 */
export const isUserPrimaryModeratorOfSchedule = (
  schedule: AnySchedule,
  userId: string | null | undefined,
): boolean => {
  if (!schedule || !userId) return false;
  if (
    Array.isArray(schedule.primary_moderator_ids) &&
    schedule.primary_moderator_ids.includes(userId)
  )
    return true;
  return schedule.moderator_id === userId;
};

/**
 * True when `schedule` has at least one primary moderator set — reading the
 * `primary_moderator_ids` array with the legacy `moderator_id` alias as a
 * fallback. A schedule carrying only a secondary moderator is NOT considered to
 * have a primary.
 */
export const scheduleHasPrimaryModerator = (schedule: AnySchedule): boolean => {
  if (!schedule) return false;
  if (
    Array.isArray(schedule.primary_moderator_ids) &&
    schedule.primary_moderator_ids.length > 0
  )
    return true;
  return String(schedule.moderator_id ?? '').trim() !== '';
};

/** Lower-cased, trimmed, space-separated status form (`no_show` -> `no show`). */
const normalizeStatus = (status: string | undefined): string =>
  String(status ?? '')
    .toLowerCase()
    .trim()
    .replace(/_/g, ' ');

/** Statuses where a moderator can still be assigned (scheduled/active/rescheduled). */
const MODERATOR_ASSIGNABLE_STATUSES = new Set([
  'scheduled',
  'active',
  'rescheduled',
]);

const isModeratorAssignable = (status: string | undefined): boolean =>
  MODERATOR_ASSIGNABLE_STATUSES.has(normalizeStatus(status));

/** Statuses that count as an upcoming, still-actionable session for a moderator. */
const UPCOMING_SESSION_STATUSES = new Set(['scheduled', 'active', 'rescheduled']);

/** Statuses that surface on the moderator's cancelled-sessions Action Center card. */
const CANCELLED_SESSION_STATUSES = new Set(['cancelled', 'canceled']);

/** Schedule row fields the primary-moderator count needs beyond the moderator ids. */
type CountableSchedule = NonNullable<AnySchedule> & {
  primary_moderator_names?: string[] | null;
  status?: string;
  participant_id?: string | number;
  session_type_id?: string | number;
  title?: string;
  created_at?: string;
  start_time?: string;
  notes?: string | null;
  session_notes?: unknown[] | null;
};

const scheduleRecency = (schedule: CountableSchedule): number =>
  new Date(schedule.created_at || schedule.start_time || 0).getTime();

/** Groups a slot by participant + session type (title as a fallback key). */
const slotKey = (schedule: CountableSchedule): string => {
  const type =
    schedule.session_type_id != null
      ? String(schedule.session_type_id)
      : String(schedule.title ?? '')
          .trim()
          .toLowerCase();
  return `${schedule.participant_id ?? ''}|${type}`;
};

/**
 * Reduce a schedule pool to the latest booking per participant+session-type
 * slot, so a rebooked/rescheduled slot's superseded rows are never counted
 * twice by the callers below.
 */
const latestBookingPerSlot = (
  schedules: CountableSchedule[],
): CountableSchedule[] => {
  const latestBySlot = new Map<string, CountableSchedule>();
  for (const schedule of schedules) {
    const key = slotKey(schedule);
    const current = latestBySlot.get(key);
    if (!current || scheduleRecency(schedule) >= scheduleRecency(current)) {
      latestBySlot.set(key, schedule);
    }
  }
  return Array.from(latestBySlot.values());
};

/**
 * Count of sessions with no primary moderator across a full schedule pool.
 * Groups rows by participant + session type and inspects only the latest
 * booking per slot. A slot counts when its latest booking is still in a
 * moderator-assignable status yet has no primary moderator set.
 */
export const countSessionsMissingPrimaryModerator = (
  schedules: CountableSchedule[],
): number =>
  latestBookingPerSlot(schedules).filter(
    (latest) =>
      isModeratorAssignable(latest.status) &&
      !scheduleHasPrimaryModerator(latest),
  ).length;

/** True when `startTime` is at or after `nowMs` (upcoming; past sessions excluded). */
const isUpcomingStart = (
  startTime: string | undefined,
  nowMs: number,
): boolean => {
  if (!startTime) return false;
  const startMs = new Date(startTime).getTime();
  return Number.isFinite(startMs) && startMs >= nowMs;
};

/**
 * Rows in `pool` where `userId` is the primary moderator and `matches` holds.
 * Callers pass either the full schedule pool or `latestBookingPerSlot(...)`,
 * depending on whether a superseded booking still counts on its own.
 */
const countPrimarySessions = (
  pool: CountableSchedule[],
  userId: string | null | undefined,
  matches: (schedule: CountableSchedule) => boolean,
): number => {
  if (!userId) return 0;
  return pool.filter(
    (schedule) =>
      isUserPrimaryModeratorOfSchedule(schedule, userId) && matches(schedule),
  ).length;
};

/**
 * Count of a moderator's own upcoming booked sessions: primary-moderator rows
 * still scheduled/active/rescheduled whose start time is at or after `nowMs`.
 * Past and completed sessions are excluded; there is no look-ahead cap.
 * Latest booking per slot only, so a rescheduled slot is never double-counted.
 */
export const countUpcomingModeratorSessions = (
  schedules: CountableSchedule[],
  userId: string | null | undefined,
  nowMs: number,
): number =>
  countPrimarySessions(
    latestBookingPerSlot(schedules),
    userId,
    (latest) =>
      UPCOMING_SESSION_STATUSES.has(normalizeStatus(latest.status)) &&
      isUpcomingStart(latest.start_time, nowMs),
  );

/**
 * Count of a moderator's cancelled sessions, with no start-time window. Like
 * countAllNoShowSessions this does NOT collapse to the latest booking per
 * slot — a cancellation that was later rebooked is still a cancellation the
 * moderator had — so this matches row-for-row with the Cancelled tab of Booked
 * Sessions, which the card deep-links to.
 */
export const countCancelledModeratorSessions = (
  schedules: CountableSchedule[],
  userId: string | null | undefined,
): number =>
  countPrimarySessions(schedules, userId, (schedule) =>
    CANCELLED_SESSION_STATUSES.has(normalizeStatus(schedule.status)),
  );

/** True when a schedule already carries a follow-up note (free-text or a note entry). */
const hasFollowUpNote = (schedule: CountableSchedule): boolean => {
  if (String(schedule.notes ?? '').trim() !== '') return true;
  return Array.isArray(schedule.session_notes) && schedule.session_notes.length > 0;
};

/**
 * Count of a moderator's no-show sessions still needing a follow-up note:
 * booked sessions where `userId` is the primary moderator, the latest booking is
 * marked no-show, and no follow-up note has been recorded yet.
 */
export const countNoShowFollowUpSessions = (
  schedules: CountableSchedule[],
  userId: string | null | undefined,
): number =>
  countPrimarySessions(
    latestBookingPerSlot(schedules),
    userId,
    (latest) =>
      normalizeStatus(latest.status) === 'no show' && !hasFollowUpNote(latest),
  );

/**
 * Count of *every* session a moderator ran that ended in a no-show — the
 * project-wide view a PM sees per moderator. Unlike the moderator's own
 * follow-up card this ignores whether a note was added and does NOT collapse to
 * the latest booking per slot: a no-show that was later rebooked is still a
 * distinct no-show the moderator had, so each no-show row where `userId` is the
 * primary moderator is counted.
 */
export const countAllNoShowSessions = (
  schedules: CountableSchedule[],
  userId: string | null | undefined,
): number =>
  countPrimarySessions(
    schedules,
    userId,
    (schedule) => normalizeStatus(schedule.status) === 'no show',
  );

/**
 * The moderator id standing for "nobody" in the per-moderator grouping.
 *
 * A sentinel rather than a separate shape so the group renders through the
 * same card code as a named moderator, and so its drill-in uses the same
 * `moderatorId=` parameter the named groups already use.
 */
export const UNASSIGNED_MODERATOR_ID = 'unassigned';

/** What the ownerless group is called wherever it is shown. */
export const UNASSIGNED_MODERATOR_LABEL = 'No Moderator Assigned';

/**
 * Cancelled and no-show sessions that had no primary moderator (OD-1343).
 *
 * The per-moderator grouping has a hole: a session nobody was assigned to
 * belongs to no card, so once it is cancelled or marked no-show it leaves the
 * Scheduler tab's "needs a moderator" list and is counted nowhere.
 *
 * "Unassigned" is `scheduleHasPrimaryModerator`, the same predicate the
 * Scheduler tab's own row uses, rather than a second definition that could
 * drift from it. The status filters and the absence of slot-collapsing match
 * `countCancelledModeratorSessions` and `countAllNoShowSessions`, so a count
 * here means the same thing as the same count under a named moderator.
 */
export const summarizeUnassignedActions = (
  schedules: CountableSchedule[],
): {cancelledCount: number; noShowCount: number} => {
  const ownerless = (schedules || []).filter(
    (schedule) => schedule && !scheduleHasPrimaryModerator(schedule),
  );
  return {
    cancelledCount: ownerless.filter((schedule) =>
      CANCELLED_SESSION_STATUSES.has(normalizeStatus(schedule.status)),
    ).length,
    noShowCount: ownerless.filter(
      (schedule) => normalizeStatus(schedule.status) === 'no show',
    ).length,
  };
};

/** A moderator's pending-action counts, as surfaced to a PM on the Moderator tab. */
export interface ModeratorActionSummary {
  moderatorId: string;
  moderatorName: string;
  upcomingCount: number;
  cancelledCount: number;
  noShowCount: number;
}

/**
 * Records one primary-moderator id/name pair into `names`: first non-empty
 * name wins; an empty name only reserves the id itself as a placeholder if
 * nothing has been recorded for that id yet.
 */
const recordModeratorName = (
  names: Map<string, string>,
  id: string,
  name: string,
): void => {
  const key = String(id);
  if (name) names.set(key, name);
  else if (!names.has(key)) names.set(key, key);
};

/**
 * Adds every primary-moderator id/name pair one `schedule` row carries to
 * `names`, plus the legacy single `moderator_id` (as its own placeholder,
 * since no name is known for it) when it hasn't been seen yet.
 */
const addScheduleModeratorNames = (
  names: Map<string, string>,
  schedule: CountableSchedule,
): void => {
  const ids = Array.isArray(schedule.primary_moderator_ids)
    ? schedule.primary_moderator_ids
    : [];
  const nameArr = Array.isArray(schedule.primary_moderator_names)
    ? schedule.primary_moderator_names
    : [];
  ids.forEach((id, index) => {
    if (!id) return;
    recordModeratorName(names, id, String(nameArr[index] ?? '').trim());
  });
  const legacyId = String(schedule.moderator_id ?? '').trim();
  if (legacyId && !names.has(legacyId)) names.set(legacyId, legacyId);
};

/**
 * Map every primary-moderator id seen across the pool to a display name, reading
 * the `primary_moderator_names[]` array the backend attaches in parallel with
 * `primary_moderator_ids[]`. Falls back to the legacy single `moderator_id`
 * (name unknown there, so the id itself stands in) and finally to the id.
 */
const collectPrimaryModeratorNames = (
  schedules: CountableSchedule[],
): Map<string, string> => {
  const names = new Map<string, string>();
  for (const schedule of schedules) {
    if (schedule) addScheduleModeratorNames(names, schedule);
  }
  return names;
};

/**
 * Per-moderator action summary the PM sees under the Moderator tab: for every
 * moderator who is the primary on at least one session in the pool, their count
 * of upcoming booked sessions (any future start time), cancelled sessions, and
 * total no-shows. Moderators with nothing pending (all counts zero) are
 * dropped, and the rest are sorted by display name so the view is stable and
 * data-driven.
 */
export const summarizeModeratorActions = (
  schedules: CountableSchedule[],
  nowMs: number,
): ModeratorActionSummary[] => {
  const nameById = collectPrimaryModeratorNames(schedules);
  const summaries: ModeratorActionSummary[] = [];
  for (const [moderatorId, moderatorName] of nameById) {
    const upcomingCount = countUpcomingModeratorSessions(
      schedules,
      moderatorId,
      nowMs,
    );
    const cancelledCount = countCancelledModeratorSessions(
      schedules,
      moderatorId,
    );
    const noShowCount = countAllNoShowSessions(schedules, moderatorId);
    if (upcomingCount === 0 && cancelledCount === 0 && noShowCount === 0) {
      continue;
    }
    summaries.push({
      moderatorId,
      moderatorName,
      upcomingCount,
      cancelledCount,
      noShowCount,
    });
  }
  return summaries.sort(
    (a, b) =>
      a.moderatorName.localeCompare(b.moderatorName) ||
      a.moderatorId.localeCompare(b.moderatorId),
  );
};

/**
 * OD-1102: statuses that count toward a participant's no-show/cancellation
 * alert limit. All variants count equally (no reason-based exclusion) — a
 * cancellation is a cancellation regardless of why, per product decision.
 */
const NO_SHOW_OR_CANCELLED_STATUSES = new Set([
  'no show',
  'cancelled',
  'canceled',
]);

/** Minimal session-type shape the no-show/cancellation limit lookup needs. */
export interface NoShowLimitSessionType {
  no_show_cancellation_limit?: number | null;
  name?: string;
}

/** Schedule row fields buildNoShowCancellationLimitEntries needs beyond CountableSchedule, for display. */
type NoShowLimitSchedule = CountableSchedule & {
  participant_first_name?: string | null;
  participant_last_name?: string | null;
  participant_email?: string | null;
  alternate_email?: string | null;
  /** OD-1130: set from the cancel dialog's "Allow future bookings" checkbox — true excludes this row from the count below. */
  no_show_limit_exempt?: boolean | null;
};

/** One (participant, session type) pair at/over its configured no-show/cancellation limit. */
export interface NoShowLimitEntry {
  participantId: string;
  participantName: string;
  participantEmail: string;
  sessionTypeId: string;
  sessionTypeName: string;
  count: number;
  limit: number;
}

/** Per-key display info buildNoShowCancellationLimitEntries carries alongside the running count. */
type NoShowCancellationMeta = {
  participantId: string;
  sessionTypeId: string;
  participantName: string;
  participantEmail: string;
};

/**
 * Extracted from buildNoShowCancellationLimitEntries: the participant +
 * session-type key for one schedule row, or null when the row doesn't count
 * toward the no-show/cancellation limit (status isn't no-show/cancelled, the
 * participant or session type is missing, that session type has no limit
 * configured, or the PM exempted this specific cancellation via "Allow
 * future bookings" — OD-1130).
 */
const noShowCancellationKeyFor = (
  schedule: NoShowLimitSchedule,
  sessionTypesById: Map<string, NoShowLimitSessionType>,
): {key: string; participantId: string; sessionTypeId: string} | null => {
  if (!NO_SHOW_OR_CANCELLED_STATUSES.has(normalizeStatus(schedule.status))) {
    return null;
  }
  if (schedule.no_show_limit_exempt) return null;
  const participantId =
    schedule.participant_id != null ? String(schedule.participant_id) : '';
  const sessionTypeId =
    schedule.session_type_id != null ? String(schedule.session_type_id) : '';
  if (!participantId || !sessionTypeId) return null;
  if (sessionTypesById.get(sessionTypeId)?.no_show_cancellation_limit == null) {
    return null;
  }
  return {key: `${participantId} ${sessionTypeId}`, participantId, sessionTypeId};
};

/**
 * Extracted from buildNoShowCancellationLimitEntries: increments one eligible
 * schedule's running count in `countsByKey`, and — the first time its key is
 * seen — records the participant's display name and email into `metaByKey`
 * for later entry-building.
 */
const recordNoShowCancellation = (
  countsByKey: Map<string, number>,
  metaByKey: Map<string, NoShowCancellationMeta>,
  schedule: NoShowLimitSchedule,
  identity: {key: string; participantId: string; sessionTypeId: string},
): void => {
  const {key, participantId, sessionTypeId} = identity;
  countsByKey.set(key, (countsByKey.get(key) ?? 0) + 1);
  if (metaByKey.has(key)) return;
  const name = `${schedule.participant_first_name ?? ''} ${schedule.participant_last_name ?? ''}`.trim();
  metaByKey.set(key, {
    participantId,
    sessionTypeId,
    participantName: name || participantId,
    participantEmail: schedule.participant_email ?? schedule.alternate_email ?? '',
  });
};

/**
 * Every (participant, session type) pair at/over that session type's
 * configured no-show/cancellation limit (OD-1102) — the Action Center card's
 * expanded "needs a decision" / "blocked" lists are built from this. A pair
 * with no limit configured (null) never appears. Computed once per schedule
 * pool in a single pass; this is a running historical total of every
 * no-show/cancellation for that participant + session type — unlike the
 * moderator counters above it does NOT collapse to the latest booking per
 * slot (a no-show that was later rebooked still counts as one of the
 * participant's no-shows). A cancellation the PM exempted via "Allow future
 * bookings" at cancel time (`schedule.no_show_limit_exempt`) never counts —
 * see OD-1130.
 */
export const buildNoShowCancellationLimitEntries = (
  schedules: NoShowLimitSchedule[],
  sessionTypesById: Map<string, NoShowLimitSessionType>,
): NoShowLimitEntry[] => {
  const countsByKey = new Map<string, number>();
  const metaByKey = new Map<string, NoShowCancellationMeta>();
  for (const schedule of schedules) {
    const identity = noShowCancellationKeyFor(schedule, sessionTypesById);
    if (identity) recordNoShowCancellation(countsByKey, metaByKey, schedule, identity);
  }
  const entries: NoShowLimitEntry[] = [];
  for (const [key, count] of countsByKey) {
    const meta = metaByKey.get(key)!;
    const sessionType = sessionTypesById.get(meta.sessionTypeId);
    const limit = sessionType?.no_show_cancellation_limit;
    if (limit == null || count < limit) continue;
    entries.push({
      participantId: meta.participantId,
      participantName: meta.participantName,
      participantEmail: meta.participantEmail,
      sessionTypeId: meta.sessionTypeId,
      sessionTypeName: sessionType?.name || meta.sessionTypeId,
      count,
      limit,
    });
  }
  return entries;
};

/**
 * Map of participantId -> session-type names for which that participant has
 * reached (or exceeded) the session type's configured no-show/cancellation
 * limit (OD-1102) — the Scheduler grid's per-row warning icon reads this.
 */
export const buildNoShowCancellationLimitFlags = (
  schedules: CountableSchedule[],
  sessionTypesById: Map<string, NoShowLimitSessionType>,
): Map<string, string[]> => {
  const flagsByParticipant = new Map<string, string[]>();
  for (const entry of buildNoShowCancellationLimitEntries(schedules, sessionTypesById)) {
    const names = flagsByParticipant.get(entry.participantId) ?? [];
    names.push(entry.sessionTypeName);
    flagsByParticipant.set(entry.participantId, names);
  }
  return flagsByParticipant;
};

/**
 * Total (participant, session type) pairs at/over their configured no-show/
 * cancellation limit (OD-1102) — the Action Center card/badge count. A
 * participant flagged for two different session types counts as two items,
 * matching how every other Action Center count (e.g. no-primary-moderator)
 * counts items to review, not distinct participants.
 */
export const countParticipantsAtNoShowCancellationLimit = (
  schedules: CountableSchedule[],
  sessionTypesById: Map<string, NoShowLimitSessionType>,
): number => buildNoShowCancellationLimitEntries(schedules, sessionTypesById).length;
